import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { MyLink } from '@/components/MyLink';
import { APP_LINKS } from '@/helpers/links';
import { useReCaptcha } from '@/providers/recaptcha';
import { useCountdownFromNow } from '@scramble/hooks';
import { CONTACT_EMAIL, OTP_LENGTH } from '@/helpers/constants';
import { Button, InlineAlert, VerificationCodeInput } from '@scramble/ui';
import { useGetPhoneVerifyStatus } from '@/query/queries/user/useGetPhoneVerifyStatus';
import { useSendPhoneVerifyCode } from '@/query/queries/user/useSendPhoneVerifyCode';
import { useSubmitPhoneVerifyCode } from '@/query/queries/user/useSubmitPhoneVerifyCode';
import IconBack from '@scramble/design-system/icons/arrow-left.svg';
import { VALIDATION_SCHEMA } from './schema';
import s from '../index.module.scss';

const SMS_LIMIT = 3;
const CHANNELS = {
  whatsapp: 'whatsapp',
  sms: 'sms',
  call: 'call',
};

export const CodeSubmitForm = ({ phone, disabled, onSuccess, onBack }) => {
  const mounted = useRef(true);
  const [success, setSuccess] = useState(false);
  const [expireTime, setExpireTime] = useState(null);
  const { data: OtpStatus } = useGetPhoneVerifyStatus(phone);
  const { mutateAsync: sendCode } = useSendPhoneVerifyCode(phone);
  const { mutateAsync: submitCode } = useSubmitPhoneVerifyCode();
  const { token: captchaToken, loaded, getCaptchaToken } = useReCaptcha();
  const { t } = useTranslation();

  const timeLeft = useCountdownFromNow(expireTime, () => {
    mounted.current && setExpireTime(null);
  });

  const onSubmit = async (values, { setErrors }) => {
    if (!loaded) return;

    const res = await submitCode({
      phone_number: phone,
      code: values.code,
    });

    if (res?.success) {
      setSuccess(true);
      onSuccess &&
        setTimeout(() => {
          onSuccess(res.data);
        }, 500); // use delay to show visual green effects
    } else {
      res?.message && setErrors({ code: res.message });
    }
  };

  const { values, errors, touched, setFieldValue, handleSubmit } = useFormik({
    onSubmit,
    validationSchema: VALIDATION_SCHEMA(t),
    initialValues: {
      code: '',
    },
  });

  const handleResend = async () => {
    if (!loaded) return;

    const token = captchaToken || (await getCaptchaToken());
    if (!token) return;

    const res = await sendCode({
      phone_number: phone,
      recaptcha_token: token,
    });

    if (res && res.success && mounted.current) {
       setFieldValue('code', '');
    }
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mounted.current) return;

    const lastCreated = OtpStatus?.last_created_at;
    if (!lastCreated) {
      setExpireTime(null);
      return;
    }

    const currentTime = moment();
    const expireTime = moment(OtpStatus.last_created_at).add(
      OtpStatus.count >= SMS_LIMIT ? 10 : 1,
        'minutes'
    );

    currentTime.isBefore(expireTime)
      ? setExpireTime(expireTime.toISOString())
      : setExpireTime(null);
    }, [OtpStatus]);

  useEffect(() => {
    if (values.code.length === OTP_LENGTH && loaded) {
      handleSubmit();
    }
  }, [values.code, handleSubmit, loaded]);

  const hasReached = OtpStatus?.count >= SMS_LIMIT;
  const canResend = !!(OtpStatus && !hasReached && !expireTime);
  const lastFourNumbers = phone.substring(phone.length - 4) || '';

  return (
    <form className={s.modal} onSubmit={handleSubmit}>
      <div className={s.header}>
        <div className="f-headline-2xs f-500">
          {t('modal:phone_verify_modal.account_security')}
        </div>
        {OtpStatus?.next_channel && (
          <div className="f-body-sm">
            {OtpStatus.next_channel === CHANNELS.whatsapp && (
              <Trans
                ns="modal"
                i18nKey="modal:phone_verify_modal.enter_verification_code_sms"
                components={{ Span: <span className="f-600" /> }}
                values={{ lastFourNumbers }}
              />
            )}
            {OtpStatus.next_channel === CHANNELS.call && (
              <Trans
                ns="modal"
                i18nKey="modal:phone_verify_modal.enter_verification_code_whatsapp"
                components={{ Span: <span className="f-600" /> }}
                values={{ lastFourNumbers }}
              />
            )}
            {OtpStatus.next_channel === CHANNELS.sms && (
              <Trans
                ns="modal"
                i18nKey="modal:phone_verify_modal.enter_verification_code_call"
                components={{ Span: <span className="f-600" /> }}
                values={{ lastFourNumbers }}
              />
            )}
          </div>
        )}
      </div>
      <div className={s.content}>
        <div className={s.input}>
          <VerificationCodeInput
            label={t('modal:phone_verify_modal.verification_code')}
            value={values.code}
            onChange={value => setFieldValue('code', value)}
            error={errors.code && touched.code}
            success={success}
            disabled={hasReached && !timeLeft}
          />
          {errors.code && touched.code && (
            <InlineAlert
              type="danger"
              title={t('validation:invalid_varification_code')}
              message={t('modal:phone_verify_modal.try_again')}
              className={s.input__error}
            />
          )}
        </div>
        {hasReached && (
          <div className={s.support_text}>
            <Trans
              ns="modal"
              i18nKey="modal:phone_verify_modal.technical_problems_support"
              components={{
                LinkEmail: (
                  <MyLink href={APP_LINKS.email} isExternal underline />
                ),
              }}
              values={{ email: CONTACT_EMAIL }}
            />
          </div>
        )}
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="tonal"
          iconUrl={IconBack}
          size="lg"
          onClick={onBack}
        />
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          onClick={handleResend}
          disabled={disabled || !canResend || !!expireTime || !OtpStatus}
          className={s.btn_send}
          label={
            expireTime ? (
              OtpStatus?.count === SMS_LIMIT ? (
                t('modal:phone_verify_modal.code_expires_with_time', {
                  timeLeft,
                })
              ) : (
                t('modal:phone_verify_modal.resend_code_with_time', {
                  timeLeft,
                })
              )
            ) : (
              <span>
                {OtpStatus?.next_channel === CHANNELS.whatsapp && (
                  <span>
                    {t('modal:phone_verify_modal.resend_code_whatsapp')}
                  </span>
                )}
                {OtpStatus?.next_channel === CHANNELS.call && (
                  <span>{t('modal:phone_verify_modal.resend_code_call')}</span>
                )}
                {OtpStatus?.next_channel === CHANNELS.sms && (
                  <span>{t('modal:phone_verify_modal.resend_code')}</span>
                )}
              </span>
            )
          }
        />
      </div>
    </form>
  );
};
